import { useState } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Plus, ChevronsUpDown, Check } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Location, Customer } from "@/types/tms";
import { AddressFields, formatAddress } from "@/components/forms/AddressFields";
import { geocodeAddress, buildAddressString } from "@/lib/geocode";
import { getLocationDisplayName } from "@/lib/location-utils";

interface LocationPickerProps {
  label: string;
  value: string | null;
  onChange: (locationId: string | null, location: Location | null) => void;
  required?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
}

interface NewLocationForm {
  customer_id: string;
  short_code: string;
  street_address: string;
  city: string;
  province_state: string;
  postal_zip_code: string;
  country: string;
  use_latlng: boolean;
  latitude_input: string;
  longitude_input: string;
}

const initialForm: NewLocationForm = {
  customer_id: "",
  short_code: "",
  street_address: "",
  city: "",
  province_state: "",
  postal_zip_code: "",
  country: "",
  use_latlng: false,
  latitude_input: "",
  longitude_input: "",
};

export function LocationPicker({ label, value, onChange, required = false, disabled = false, disabledTooltip }: LocationPickerProps) {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [formData, setFormData] = useState<NewLocationForm>(initialForm);

  const { data: locations } = useQuery({
    // Distinct key: other surfaces cache ["locations"] WITHOUT the customer
    // join, which made the label flip between "Customer - CODE" and "CODE".
    // Prefix invalidations of ["locations"] still refresh this entry.
    queryKey: ["locations", "with-customer"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("locations")
        .select("*, customer:customers(*)")
        .eq("archived", false)
        .order("short_code");
      if (error) throw error;
      return data as Location[];
    },
  });

  const { data: customers } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => {
      const { data, error } = await supabase.from("customers").select("*").order("organization_name");
      if (error) throw error;
      return data as Customer[];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: NewLocationForm) => {
      // Resolve coordinates up-front so the insert satisfies the NOT NULL
      // origin/destination columns. Quick-create from the order form sets
      // origin and destination to the same value; the user can split them
      // afterwards on the Locations page if needed.
      let lat: number | null = null;
      let lng: number | null = null;
      if (data.use_latlng) {
        const parsedLat = parseFloat(data.latitude_input);
        const parsedLng = parseFloat(data.longitude_input);
        if (!isNaN(parsedLat) && !isNaN(parsedLng)) {
          lat = parsedLat;
          lng = parsedLng;
        }
      } else {
        const addressStr = buildAddressString(data);
        if (addressStr) {
          const coords = await geocodeAddress(addressStr);
          if (coords) {
            lat = coords.latitude;
            lng = coords.longitude;
          }
        }
      }
      if (lat == null || lng == null) {
        throw new Error(
          data.use_latlng
            ? "Latitude and longitude are required"
            : "Could not geocode address — enter Lat/Lng manually instead"
        );
      }
      const { data: newLocation, error } = await supabase
        .from("locations")
        .insert({
          customer_id: data.customer_id || null,
          short_code: data.short_code,
          street_address: data.street_address,
          city: data.city,
          province_state: data.province_state,
          postal_zip_code: data.postal_zip_code,
          country: data.country,
          latitude: lat,
          longitude: lng,
          origin_latitude: lat,
          origin_longitude: lng,
          destination_latitude: lat,
          destination_longitude: lng,
        })
        .select("*, customer:customers(*)")
        .single();
      if (error) throw error;
      return newLocation as Location;
    },
    onSuccess: (newLocation) => {
      queryClient.invalidateQueries({ queryKey: ["locations"] });
      toast.success("Location created successfully");
      onChange(newLocation.id, newLocation);
      setFormData(initialForm);
      setIsDialogOpen(false);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.street_address.trim() || !formData.city.trim()) {
      toast.error("Street address and city are required so orders using this location validate correctly");
      return;
    }
    if (formData.use_latlng) {
      const lat = parseFloat(formData.latitude_input);
      const lng = parseFloat(formData.longitude_input);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        toast.error("Latitude must be a number between -90 and 90");
        return;
      }
      if (isNaN(lng) || lng < -180 || lng > 180) {
        toast.error("Longitude must be a number between -180 and 180");
        return;
      }
    }
    createMutation.mutate(formData);
  };

  const handleLocationChange = (locationId: string) => {
    const selectedLocation = locations?.find((l) => l.id === locationId) || null;
    onChange(locationId, selectedLocation);
  };

  const selectedLocation = locations?.find((l) => l.id === value) || null;
  const selectedDisplayName = selectedLocation ? getLocationDisplayName(selectedLocation) : null;
  const selectedAddress = selectedLocation ? formatAddress(selectedLocation) : null;
  const selectedFullText = selectedDisplayName
    ? selectedAddress
      ? `${selectedDisplayName} — ${selectedAddress}`
      : selectedDisplayName
    : null;

  const renderCombobox = (isDisabled: boolean) => (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={isPopoverOpen}
          disabled={isDisabled}
          className={cn(
            "w-full min-w-0 max-w-full justify-between overflow-hidden font-normal",
            isDisabled ? "w-full" : "flex-1",
            !selectedDisplayName && "text-muted-foreground",
          )}
        >
          <span className="truncate block text-left flex-1 min-w-0">
            {selectedFullText ?? "Select location"}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <Command>
          <CommandInput placeholder="Search locations..." />
          <CommandList className="max-h-[260px] overflow-y-auto">
            <CommandEmpty>No locations found.</CommandEmpty>
            <CommandGroup>
              {locations?.map((location) => {
                const displayName = getLocationDisplayName(location);
                const address = formatAddress(location);
                return (
                  <CommandItem
                    key={location.id}
                    value={`${displayName} ${address}`}
                    onSelect={() => {
                      handleLocationChange(location.id);
                      setIsPopoverOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 shrink-0",
                        value === location.id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <div className="flex flex-col min-w-0">
                      <span className="font-medium truncate">{displayName}</span>
                      <span className="text-xs text-muted-foreground truncate">{address}</span>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );

  return (
    <div className="space-y-2 min-w-0">
      <Label>{label} {required && "*"}</Label>
      <div className="flex min-w-0 items-start gap-2">
        {disabled && disabledTooltip ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex-1 min-w-0">
                  {renderCombobox(true)}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{disabledTooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : selectedFullText ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex-1 min-w-0">
                  {renderCombobox(false)}
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <p className="font-medium">{selectedDisplayName}</p>
                {selectedAddress && <p className="text-xs text-muted-foreground">{selectedAddress}</p>}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <div className="flex-1 min-w-0">
            {renderCombobox(disabled)}
          </div>
        )}
        
        {!disabled && (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button type="button" variant="outline" size="icon">
              <Plus className="h-4 w-4" />
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Location</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Customer</Label>
                  <Select value={formData.customer_id} onValueChange={(v) => setFormData({ ...formData, customer_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>
                      {customers?.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.organization_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="location-short-code">Short Code *</Label>
                  <Input
                    id="location-short-code"
                    value={formData.short_code}
                    onChange={(e) => setFormData({ ...formData, short_code: e.target.value })}
                    placeholder="e.g., ELP1"
                    required
                  />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Address</Label>
                <div className="flex items-center gap-2">
                  <Label htmlFor="picker-use-latlng" className="cursor-pointer text-sm font-normal">Use Lat/Lng</Label>
                  <Switch
                    id="picker-use-latlng"
                    checked={formData.use_latlng}
                    onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, use_latlng: checked }))}
                  />
                </div>
              </div>
              {formData.use_latlng ? (
                <div className="space-y-4">
                  <AddressFields
                    prefix="new-location"
                    values={{
                      street_address: formData.street_address,
                      city: formData.city,
                      province_state: formData.province_state,
                      postal_zip_code: formData.postal_zip_code,
                      country: formData.country,
                    }}
                    onChange={(field, val) => setFormData((prev) => ({ ...prev, [field]: val }))}
                    onBatchChange={(updates) => setFormData((prev) => ({ ...prev, ...updates }))}
                    required
                  />
                  <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="picker-latitude">Latitude *</Label>
                    <Input
                      id="picker-latitude"
                      value={formData.latitude_input}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v.includes(",")) {
                          const [lat, lng] = v.split(",").map((s) => s.trim());
                          setFormData((prev) => ({ ...prev, latitude_input: lat, longitude_input: lng ?? prev.longitude_input }));
                        } else {
                          setFormData((prev) => ({ ...prev, latitude_input: v }));
                        }
                      }}
                      placeholder="34.052235"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="picker-longitude">Longitude *</Label>
                    <Input
                      id="picker-longitude"
                      value={formData.longitude_input}
                      onChange={(e) => setFormData((prev) => ({ ...prev, longitude_input: e.target.value }))}
                      placeholder="-118.243683"
                      required
                    />
                  </div>
                  </div>
                </div>
              ) : (
                <AddressFields
                  prefix="new-location"
                  values={{
                    street_address: formData.street_address,
                    city: formData.city,
                    province_state: formData.province_state,
                    postal_zip_code: formData.postal_zip_code,
                    country: formData.country,
                  }}
                  onChange={(field, val) => setFormData((prev) => ({ ...prev, [field]: val }))}
                  onBatchChange={(updates) => setFormData((prev) => ({ ...prev, ...updates }))}
                  required
                />
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? "Creating..." : "Create Location"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>
    </div>
  );
}
